import {
  Box,
  CircularProgress,
  styled,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Chip,
  Tooltip,
  keyframes,
} from '@mui/material'
import moment from 'moment/moment'
import lightTheme from '../../styles/theme/lightTheme'
import * as React from 'react'
import PropTypes from 'prop-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEye, faEdit, faTrashCan } from '@fortawesome/pro-regular-svg-icons'
import AppPagination from '../paginations'
import {
  faCopy,
  faHandshake,
  faEllipsisVertical,
} from '@fortawesome/pro-solid-svg-icons'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ROLE_CANDIDAT,
  ROLE_ADMIN,
  ROLE_MINI_ADMIN,
  ROLE_SUPER_ADMIN,
} from '../../data'
import { CustomButton } from '../buttons'

function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`full-width-tabpanel-${index}`}
      aria-labelledby={`full-width-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography>{children}</Typography>
        </Box>
      )}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
}

const TablePanelBox = styled(TabPanel)(({ theme }) => ({
  height: 'calc(100% - 45px)',
  marginBottom: 10,
  '& .MuiBox-root': {
    padding: 0,
  },
}))

const HeaderTab = styled(TableHead)(({ theme }) => ({
  height: 50,
  backgroundColor: lightTheme.palette.primary.b6,
}))

const TitleHead = styled(TableCell)(({ theme }) => ({
  fontFamily: 'Century Gothic Bold',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  whiteSpace: 'nowrap',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const BodyTable = styled(TableBody)(({ theme }) => ({
  backgroundColor: '#FFFFFF',
}))

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  borderBottom: '2px solid rgb(227, 230, 255, 0.5)',
  fontFamily: 'Century Gothic',
  fontSize: 10,
  lineHeight: '140%',
  letterSpacing: '0.2px',
  color: lightTheme.palette.text.primary,
  verticalAlign: 'top',

  [theme.breakpoints.up('sm')]: {
    fontSize: 12,
  },
}))

const ButtonIcon = styled(Box)(({ theme }) => ({
  width: 40,
  height: 40,
  border: `1px solid ${lightTheme.palette.primary.b2}`,
  borderRadius: '8px',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  margin: '0 4px',
  transition: 'border-color 0.2s ease, background-color 0.2s ease',
  '&:hover': {
    border: `2px solid ${lightTheme.palette.primary.b2}`,
    backgroundColor: 'rgba(102, 126, 234, 0.06)',
  },
}))

const StyledActionMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 16,
    minWidth: 220,
    border: '1px solid rgba(99, 102, 241, 0.15)',
    boxShadow: '0 24px 60px rgba(99, 102, 241, 0.18)',
  },
}))

const StyledActionMenuItem = styled(MenuItem)(({ theme }) => ({
  px: 2.5,
  py: 1.25,
  borderRadius: 12,
  gap: 10,
  color: lightTheme.palette.text.primary,
  fontFamily: 'Century Gothic',
  fontSize: 14,
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: 'rgba(102, 126, 234, 0.1)',
  },
  '& .menuActionIcon': {
    minWidth: 24,
    color: '#667eea',
  },
}))

const pulse = keyframes`
  0% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0.7);
  }
  70% {
    transform: scale(1.1);
    box-shadow: 0 0 0 6px rgba(76, 175, 80, 0);
  }
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(76, 175, 80, 0);
  }
`

const LiveBadge = styled(Chip)(({ theme }) => ({
  height: 10,
  minWidth: 10,
  fontSize: '10px',
  fontWeight: 600,
  backgroundColor: '#4CAF50',
  color: '#FFFFFF',
  marginRight: 4,
  flexShrink: 0,
  animation: `${pulse} 1.5s infinite`,
  '& .MuiChip-label': {
    padding: '0 4px',
  },
  [theme.breakpoints.up('sm')]: {
    fontSize: '11px',
  },
}))

const getPostTitle = (besoin, language) =>
  language === 'fr'
    ? '#' + besoin.id + ' ' + besoin.poste?.titre
    : '#' + besoin.id + ' ' + besoin.poste?.titreEn || besoin.poste?.titre

const MetaLine = ({ label, value }) => (
  <Box
    sx={{
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 0.5,
    }}
  >
    <Typography variant="caption" sx={{ fontWeight: 600 }}>
      {label}:
    </Typography>
    <Typography variant="caption">
      {value}
    </Typography>
  </Box>
)

export default function BesoinTable({
  besoins,
  loading,
  page,
  count,
  handlePageChange,
  handleRemove,
  handleCopy,
  handleOpenUserCvsModal,
  showDetail = false,
  role,
  pageSize = 10,
}) {
  const { t, i18n } = useTranslation()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = React.useState(null)
  const [menuBesoin, setMenuBesoin] = React.useState(null)

  const isCandidate = role === ROLE_CANDIDAT
  const isAdmin =
    role === ROLE_SUPER_ADMIN || role === ROLE_MINI_ADMIN || role === ROLE_ADMIN
  const showCompany = !isCandidate && showDetail

  const handleMenuOpen = (event, besoin) => {
    setAnchorEl(event.currentTarget)
    setMenuBesoin(besoin)
  }

  const handleMenuClose = () => {
    setAnchorEl(null)
    setMenuBesoin(null)
  }

  const navigateToBesoin = (besoin) => {
    if (role === 'ROLE_ADMIN') {
      navigate('/admin/gestion-client/matching/' + besoin.id)
    } else {
      navigate('/offres-emploi/' + besoin.id)
    }
    handleMenuClose()
  }

  const navigateToMatching = (besoin) => {
    navigate(
      role === 'ROLE_ADMIN'
        ? '/admin/gestion-client/matching/' + besoin.id
        : '/user/candidat/matching/' + besoin.id
    )
    handleMenuClose()
  }

  const navigateToView = (besoin) => {
    navigate('/offres-emploi/' + besoin.id)
    handleMenuClose()
  }

  const colSpan =
    3 + (showCompany ? 1 : 0) + (showDetail ? 1 : 0) + (isCandidate ? 0 : 2) + 1

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 120,
          py: 4,
        }}
      >
        <CircularProgress sx={{ color: '#667eea' }} />
      </Box>
    )
  }

  const hasItems = besoins && besoins.length > 0

  return (
    <>
      <TablePanelBox value={0} index={0}>
        <Box sx={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
          <Table
            stickyHeader
            aria-label="besoins table"
            sx={{ minWidth: { xs: 280, sm: 'auto' } }}
          >
            <HeaderTab>
              <TableRow>
                {showCompany && (
                  <TitleHead sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    {t('header.company')}
                  </TitleHead>
                )}
                <TitleHead sx={{ minWidth: { xs: 160, sm: 'auto' } }}>
                  {t('filter.poste')}
                </TitleHead>
                <TitleHead
                  sx={{
                    textAlign: 'center',
                    display: { xs: 'none', md: 'table-cell' },
                  }}
                >
                  {t('filter.place')}
                </TitleHead>
                {showDetail && (
                  <TitleHead
                    sx={{
                      textAlign: 'center',
                      display: { xs: 'none', lg: 'table-cell' },
                    }}
                  >
                    {t('contact.country')}
                  </TitleHead>
                )}
                <TitleHead
                  sx={{
                    textAlign: 'center',
                    display: { xs: 'none', sm: 'table-cell' },
                  }}
                >
                  {t('profile.publicationDate')}
                </TitleHead>
                {!isCandidate && (
                  <TitleHead
                    sx={{
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}
                  >
                    {t('profile.matching')}
                  </TitleHead>
                )}
                {!isCandidate && (
                  <TitleHead
                    sx={{
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'table-cell' },
                    }}
                  >
                    {t('profile.dateExpire')}
                  </TitleHead>
                )}
                <TitleHead
                  sx={{
                    width: { xs: 56, sm: isCandidate ? 140 : 200 },
                    textAlign: 'center',
                  }}
                >
                  {t('profile.actions')}
                </TitleHead>
              </TableRow>
            </HeaderTab>
            <BodyTable>
              {hasItems ? (
                besoins.map((besoin) => (
                  <TableRow key={besoin.id}>
                    {showCompany && (
                      <StyledTableCell
                        sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                      >
                        <Typography
                          component="a"
                          href={
                            '/admin/gestion-client/profil-client-entreprise/' +
                            besoin?.entreprise?.profile?.id
                          }
                          variant="body2"
                          sx={{
                            color: '#667eea',
                            fontWeight: 600,
                            textDecoration: 'none',
                            '&:hover': { textDecoration: 'underline' },
                          }}
                        >
                          {besoin.entreprise?.raisonSocial}
                        </Typography>
                      </StyledTableCell>
                    )}
                    <StyledTableCell sx={{ minWidth: { xs: 160, sm: 'auto' } }}>
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.75,
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 0.5,
                          }}
                        >
                          {besoin.postuler && <LiveBadge label=" " />}
                          <Typography
                            variant="body2"
                            onClick={() => navigateToBesoin(besoin)}
                            sx={{
                              color: '#667eea',
                              cursor: 'pointer',
                              fontWeight: 600,
                              lineHeight: 1.35,
                              '&:hover': {
                                color: '#5a67d8',
                                textDecoration: 'underline',
                              },
                            }}
                          >
                            {getPostTitle(besoin, i18n.language)}
                          </Typography>
                        </Box>

                        <Box
                          sx={{
                            display: { xs: 'flex', md: 'none' },
                            flexDirection: 'column',
                            gap: 0.5,
                          }}
                        >
                          {showCompany && besoin.entreprise?.raisonSocial && (
                            <MetaLine
                              label={t('header.company')}
                              value={besoin.entreprise.raisonSocial}
                            />
                          )}
                          <MetaLine
                            label={t('filter.place')}
                            value={besoin.lieu}
                          />
                          <MetaLine
                            label={t('profile.publicationDate')}
                            value={moment(besoin.date)
                              .tz('Europe/Paris')
                              .format('DD/MM/YYYY')}
                          />
                          {!isCandidate && (
                            <MetaLine
                              label={t('profile.matching')}
                              value={besoin.totalMatch}
                            />
                          )}
                          {!isCandidate &&
                            besoin.postuler &&
                            besoin.dateExpiration && (
                              <MetaLine
                                label={t('profile.dateExpire')}
                                value={moment(besoin.dateExpiration)
                                  .tz('Europe/Paris')
                                  .format('DD/MM/YYYY')}
                              />
                            )}
                          {showDetail && besoin.pays && (
                            <MetaLine
                              label={t('contact.country')}
                              value={besoin.pays}
                            />
                          )}
                        </Box>
                      </Box>
                    </StyledTableCell>
                    <StyledTableCell
                      sx={{
                        textAlign: 'center',
                        display: { xs: 'none', md: 'table-cell' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {besoin.lieu}
                      </Typography>
                    </StyledTableCell>
                    {showDetail && (
                      <StyledTableCell
                        sx={{
                          textAlign: 'center',
                          display: { xs: 'none', lg: 'table-cell' },
                        }}
                      >
                        {besoin.pays}
                      </StyledTableCell>
                    )}
                    <StyledTableCell
                      sx={{
                        textAlign: 'center',
                        display: { xs: 'none', sm: 'table-cell' },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {moment(besoin.date)
                          .tz('Europe/Paris')
                          .format('DD/MM/YYYY HH:mm')}
                      </Typography>
                    </StyledTableCell>
                    {!isCandidate && (
                      <StyledTableCell
                        sx={{
                          textAlign: 'center',
                          display: { xs: 'none', sm: 'table-cell' },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {besoin.totalMatch}
                        </Typography>
                      </StyledTableCell>
                    )}
                    {!isCandidate && (
                      <StyledTableCell
                        sx={{
                          textAlign: 'center',
                          display: { xs: 'none', sm: 'table-cell' },
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {besoin.postuler && besoin.dateExpiration
                            ? moment(besoin.dateExpiration)
                                .tz('Europe/Paris')
                                .format('DD/MM/YYYY')
                            : '—'}
                        </Typography>
                      </StyledTableCell>
                    )}
                    <StyledTableCell>
                      <Box
                        sx={{
                          display: { xs: 'flex', sm: 'none' },
                          justifyContent: 'center',
                        }}
                      >
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, besoin)}
                          aria-label={t('profile.actions')}
                        >
                          <FontAwesomeIcon
                            icon={faEllipsisVertical}
                            color={lightTheme.palette.primary.b2}
                            fontSize={16}
                          />
                        </IconButton>
                      </Box>

                      <Box
                        sx={{
                          display: { xs: 'none', sm: 'flex' },
                          justifyContent: 'center',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 0.5,
                        }}
                      >
                        {!isCandidate && (
                          <Tooltip title={t('profile.matching')} arrow>
                            <ButtonIcon
                              onClick={() => navigateToMatching(besoin)}
                            >
                              <FontAwesomeIcon
                                icon={faHandshake}
                                fontSize={16}
                                color={lightTheme.palette.primary.b2}
                              />
                            </ButtonIcon>
                          </Tooltip>
                        )}
                        <Tooltip title={t('button.view')} arrow>
                          <ButtonIcon onClick={() => navigateToView(besoin)}>
                            <FontAwesomeIcon
                              icon={faEye}
                              fontSize={16}
                              color={lightTheme.palette.primary.b2}
                            />
                          </ButtonIcon>
                        </Tooltip>
                        {isCandidate && besoin.postuler && (
                          <CustomButton
                            displayIcon="none"
                            height={40}
                            handleClick={() => {
                              handleOpenUserCvsModal(besoin.id)
                            }}
                          >
                            {t('button.postuler')}
                          </CustomButton>
                        )}
                        {!isCandidate && (
                          <Tooltip title={t('profile.actions')} arrow>
                            <ButtonIcon
                              onClick={(e) => handleMenuOpen(e, besoin)}
                            >
                              <FontAwesomeIcon
                                icon={faEllipsisVertical}
                                fontSize={16}
                                color={lightTheme.palette.primary.b2}
                              />
                            </ButtonIcon>
                          </Tooltip>
                        )}
                      </Box>

                      <StyledActionMenu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl) && menuBesoin === besoin}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                        PaperProps={{
                          sx: {
                            p: 1,
                            bgcolor: '#fff',
                          },
                        }}
                      >
                        {!isCandidate && (
                          <StyledActionMenuItem
                            onClick={() => navigateToMatching(besoin)}
                          >
                            <FontAwesomeIcon
                              icon={faHandshake}
                              className="menuActionIcon"
                              fontSize={14}
                            />
                            <Typography variant="body2">
                              {t('profile.matching')}
                            </Typography>
                          </StyledActionMenuItem>
                        )}
                        <StyledActionMenuItem
                          onClick={() => navigateToView(besoin)}
                        >
                          <FontAwesomeIcon
                            icon={faEye}
                            className="menuActionIcon"
                            fontSize={14}
                          />
                          <Typography variant="body2">
                            {t('button.view')}
                          </Typography>
                        </StyledActionMenuItem>
                        {isCandidate && besoin.postuler && (
                          <StyledActionMenuItem
                            onClick={() => {
                              handleOpenUserCvsModal(besoin.id)
                              handleMenuClose()
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faHandshake}
                              className="menuActionIcon"
                              fontSize={14}
                            />
                            <Typography variant="body2">
                              {t('button.postuler')}
                            </Typography>
                          </StyledActionMenuItem>
                        )}
                        {!isCandidate && (
                          <>
                            <StyledActionMenuItem
                              onClick={() => {
                                navigate(
                                  isAdmin
                                    ? '/admin/annonces/edit/' +
                                        besoin.entreprise?.profile.id +
                                        '/' +
                                        besoin.entreprise?.id +
                                        '/' +
                                        besoin.id
                                    : '/user/annonces/edit/' + besoin.id
                                )
                                handleMenuClose()
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faEdit}
                                className="menuActionIcon"
                                fontSize={14}
                              />
                              <Typography variant="body2">
                                {t('button.edit')}
                              </Typography>
                            </StyledActionMenuItem>
                            <StyledActionMenuItem
                              onClick={() => {
                                handleCopy(besoin)
                                handleMenuClose()
                              }}
                            >
                              <FontAwesomeIcon
                                icon={faCopy}
                                className="menuActionIcon"
                                fontSize={14}
                              />
                              <Typography variant="body2">
                                {t('button.copy')}
                              </Typography>
                            </StyledActionMenuItem>
                            <StyledActionMenuItem
                              onClick={() => {
                                handleRemove(besoin.id)
                                handleMenuClose()
                              }}
                              sx={{ color: 'error.main' }}
                            >
                              <FontAwesomeIcon
                                icon={faTrashCan}
                                className="menuActionIcon"
                                fontSize={14}
                                style={{ color: 'inherit' }}
                              />
                              <Typography
                                variant="body2"
                                sx={{ color: 'inherit' }}
                              >
                                {t('button.delete')}
                              </Typography>
                            </StyledActionMenuItem>
                          </>
                        )}
                      </StyledActionMenu>
                    </StyledTableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <StyledTableCell
                    colSpan={colSpan}
                    align="center"
                    sx={{ py: 4 }}
                  >
                    <Typography variant="body2">
                      {t('jobOffers.noResults')}
                    </Typography>
                  </StyledTableCell>
                </TableRow>
              )}
            </BodyTable>
          </Table>
        </Box>
      </TablePanelBox>
      {count > pageSize && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 2,
            px: { xs: 1, sm: 0 },
          }}
        >
          <AppPagination
            handlePageChange={handlePageChange}
            count={count}
            page={page}
            pageSize={pageSize}
          />
        </Box>
      )}
    </>
  )
}
